import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation, faHeartPulse, faFire, faShieldHalved,
  faLocationDot, faCheckCircle, faBell, faRefresh, faCircleCheck,
  faSpinner, faUser, faBuilding, faPersonWalking
} from '@fortawesome/free-solid-svg-icons';
import NavigationSecurity from '../Layouts/Navigation/NavigationSecurity';
import { BASE_URL } from '../utils/constants';

/* ── helpers ── */
const getUserId = () => {
  try {
    const t = localStorage.getItem('token');
    if (!t) return null;
    return JSON.parse(atob(t.split('.')[1])).id_usuario;
  } catch { return null; }
};

const TIPOS = {
  panico:    { label: 'Pánico',               icon: faTriangleExclamation, color: 'bg-red-600',    badge: 'bg-red-100 text-red-700 border-red-200',        bar: 'border-red-500'    },
  medica:    { label: 'Emergencia Médica',    icon: faHeartPulse,          color: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700 border-orange-200', bar: 'border-orange-500' },
  incendio:  { label: 'Incendio',             icon: faFire,                color: 'bg-amber-500',  badge: 'bg-amber-100 text-amber-700 border-amber-200',    bar: 'border-amber-500'  },
  seguridad: { label: 'Situación Seguridad',  icon: faShieldHalved,        color: 'bg-blue-700',   badge: 'bg-blue-100 text-blue-700 border-blue-200',       bar: 'border-blue-600'   },
};

const ESTADO_BADGE = {
  activa:    'bg-red-100 text-red-700 border border-red-200 animate-pulse',
  en_camino: 'bg-purple-100 text-purple-700 border border-purple-200',
  atendida:  'bg-yellow-100 text-yellow-700 border border-yellow-200',
  resuelta:  'bg-green-100 text-green-700 border border-green-200',
};

const ESTADO_LABEL = {
  activa:    'Activa',
  en_camino: 'En camino',
  atendida:  'Atendida',
  resuelta:  'Resuelta',
};

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${dt.getDate()} ${months[dt.getMonth()]} · ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
};

const timeAgo = (d) => {
  const secs = (Date.now() - new Date(d)) / 1000;
  if (secs < 60) return 'hace un momento';
  if (secs < 3600) return `hace ${Math.floor(secs / 60)} min`;
  if (secs < 86400) return `hace ${Math.floor(secs / 3600)} h`;
  return `hace ${Math.floor(secs / 86400)} días`;
};

const Snack = ({ msg, type, onClose }) => {
  if (!msg) return null;
  const colors = { success: 'bg-green-600', error: 'bg-red-600', info: 'bg-blue-600' };
  return (
    <div className={`fixed top-5 right-5 z-50 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 ${colors[type] || colors.info}`}>
      <span>{msg}</span>
      <button onClick={onClose} className="font-bold">✕</button>
    </div>
  );
};

export default function SecurityEmergencias() {
  const userId = getUserId();
  const [activas, setActivas] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('activas'); // 'activas' | 'historial'
  const [actionLoading, setActionLoading] = useState(null);
  const [snack, setSnack] = useState({ msg: '', type: 'info' });
  const prevCount = useRef(0);
  const audioRef = useRef(null);

  const showSnack = (msg, type = 'info') => {
    setSnack({ msg, type });
    setTimeout(() => setSnack({ msg: '' }), 4000);
  };

  const loadActivas = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/emergencias/activas`);
      const data = res.data;
      // Alerta sonora si llega una nueva emergencia activa
      if (data.length > prevCount.current && prevCount.current >= 0) {
        showSnack(`🚨 ¡Nueva emergencia activa! (${data.length} total)`, 'error');
      }
      prevCount.current = data.length;
      setActivas(data);
    } catch {
      // silencioso en polling
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistorial = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/emergencias?limit=50`);
      setHistorial(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    loadActivas();
    loadHistorial();
    const interval = setInterval(() => {
      loadActivas();
      loadHistorial();
    }, 10000); // poll cada 10 segundos
    return () => clearInterval(interval);
  }, [loadActivas, loadHistorial]);

  const handleEnCamino = async (id) => {
    if (!userId) return;
    setActionLoading(id + '_encamino');
    try {
      await axios.put(`${BASE_URL}/emergencias/${id}/en-camino`, { id_usuario_atiende: userId });
      showSnack('En camino a atender la emergencia', 'info');
      await loadActivas();
      await loadHistorial();
    } catch (err) {
      showSnack(err.response?.data?.error || 'Error al actualizar', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAtender = async (id) => {
    if (!userId) return;
    setActionLoading(id + '_atender');
    try {
      await axios.put(`${BASE_URL}/emergencias/${id}/atender`, { id_usuario_atiende: userId });
      showSnack('✅ Emergencia marcada como atendida', 'success');
      await loadActivas();
      await loadHistorial();
    } catch (err) {
      showSnack(err.response?.data?.error || 'Error al atender', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResolver = async (id) => {
    if (!userId) return;
    setActionLoading(id + '_resolver');
    try {
      await axios.put(`${BASE_URL}/emergencias/${id}/resolver`, { id_usuario_resuelve: userId });
      showSnack('✅ Emergencia resuelta', 'success');
      await loadActivas();
      await loadHistorial();
    } catch (err) {
      showSnack(err.response?.data?.error || 'Error al resolver', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const EmergencyCard = ({ e, showActions = true }) => {
    const meta = TIPOS[e.tipo] || TIPOS.panico;
    return (
      <div className={`bg-white rounded-2xl shadow-md border-l-4 ${meta.bar} p-5 space-y-3`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full ${meta.color} flex items-center justify-center text-white shadow-md flex-shrink-0`}>
              <FontAwesomeIcon icon={meta.icon} className="text-xl" />
            </div>
            <div>
              <p className="font-bold text-gray-800">{meta.label}</p>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize border ${ESTADO_BADGE[e.estado]}`}>
                {ESTADO_LABEL[e.estado] || e.estado}
              </span>
            </div>
          </div>
          <span className="text-xs text-gray-400 flex-shrink-0">{timeAgo(e.fecha_emergencia)}</span>
        </div>

        {/* Info del residente */}
        <div className="bg-gray-50 rounded-xl p-3 space-y-1">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <FontAwesomeIcon icon={faUser} className="text-gray-400 w-4" />
            <span className="font-semibold">{e.usuario_nombre} {e.usuario_apellido}</span>
            {e.usuario_telefono && <span className="text-gray-500">— {e.usuario_telefono}</span>}
          </div>
          {e.num_apartamento && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FontAwesomeIcon icon={faBuilding} className="text-gray-400 w-4" />
              <span>Apto {e.num_apartamento}{e.torre_nombre ? ` · Torre ${e.torre_nombre}` : ''}</span>
            </div>
          )}
          {e.descripcion && (
            <p className="text-sm text-gray-600 italic mt-1">"{e.descripcion}"</p>
          )}
        </div>

        {/* Ubicación */}
        {e.latitud && e.longitud && (
          <a
            href={`https://www.google.com/maps?q=${e.latitud},${e.longitud}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
          >
            <FontAwesomeIcon icon={faLocationDot} />
            <span>Ver ubicación del residente en el mapa</span>
          </a>
        )}

        {/* Atendida por */}
        {e.atendido_por_nombre && (
          <p className="text-xs text-green-600">
            Atendida por: {e.atendido_por_nombre} {e.atendido_por_apellido} · {fmtDate(e.fecha_atencion)}
          </p>
        )}

        {/* Acciones */}
        {showActions && e.estado !== 'resuelta' && (
          <div className="flex gap-2 pt-1 flex-wrap">
            {e.estado === 'activa' && (
              <button
                onClick={() => handleEnCamino(e.id_emergencia)}
                disabled={actionLoading === e.id_emergencia + '_encamino'}
                className="flex-1 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
              >
                {actionLoading === e.id_emergencia + '_encamino'
                  ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  : <><FontAwesomeIcon icon={faPersonWalking} className="mr-1" />En camino</>}
              </button>
            )}
            {(e.estado === 'en_camino') && (
              <button
                onClick={() => handleAtender(e.id_emergencia)}
                disabled={actionLoading === e.id_emergencia + '_atender'}
                className="flex-1 py-2 rounded-xl bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
              >
                {actionLoading === e.id_emergencia + '_atender'
                  ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  : 'Atendiendo'}
              </button>
            )}
            {(e.estado === 'en_camino' || e.estado === 'atendida') && (
              <button
                onClick={() => handleResolver(e.id_emergencia)}
                disabled={actionLoading === e.id_emergencia + '_resolver'}
                className="flex-1 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
              >
                {actionLoading === e.id_emergencia + '_resolver'
                  ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  : <><FontAwesomeIcon icon={faCircleCheck} className="mr-1" />Resolver</>}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <NavigationSecurity />
      <Snack msg={snack.msg} type={snack.type} onClose={() => setSnack({ msg: '' })} />

      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-3xl mx-auto space-y-6">

          {/* Header */}
          <div className="bg-red-600 rounded-2xl p-6 text-white shadow-lg flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faTriangleExclamation} className="text-3xl" />
                <h1 className="text-2xl font-bold">Panel de Emergencias</h1>
              </div>
              <p className="text-red-100 text-sm mt-1">Monitoreo en tiempo real · Actualiza cada 10 segundos</p>
            </div>
            <button
              onClick={() => { loadActivas(); loadHistorial(); }}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <FontAwesomeIcon icon={faRefresh} />
              Actualizar
            </button>
          </div>

          {/* Estado activo: alerta si hay emergencias */}
          {activas.length > 0 && (
            <div className="bg-red-50 border-2 border-red-400 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
              <FontAwesomeIcon icon={faBell} className="text-red-600 text-2xl" />
              <div>
                <p className="font-bold text-red-700">{activas.length} emergencia{activas.length !== 1 ? 's' : ''} activa{activas.length !== 1 ? 's' : ''}</p>
                <p className="text-red-500 text-sm">Requiere atención inmediata</p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 bg-white rounded-xl shadow-sm p-1">
            <button
              onClick={() => setTab('activas')}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'activas' ? 'bg-red-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              🚨 Activas {activas.length > 0 && <span className="ml-1 bg-white text-red-600 rounded-full px-2 py-0.5 text-xs font-bold">{activas.length}</span>}
            </button>
            <button
              onClick={() => setTab('historial')}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'historial' ? 'bg-gray-700 text-white shadow' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              📋 Historial
            </button>
          </div>

          {/* Contenido */}
          {loading ? (
            <div className="flex justify-center py-16">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-red-400" />
            </div>
          ) : tab === 'activas' ? (
            activas.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-md p-12 text-center">
                <FontAwesomeIcon icon={faCheckCircle} className="text-5xl text-green-400 mb-3" />
                <p className="text-gray-500 font-semibold">No hay emergencias activas</p>
                <p className="text-gray-400 text-sm mt-1">El conjunto está en calma</p>
              </div>
            ) : (
              <div className="space-y-4">
                {activas.map(e => <EmergencyCard key={e.id_emergencia} e={e} showActions />)}
              </div>
            )
          ) : (
            historial.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-md p-12 text-center">
                <p className="text-gray-400">No hay registros</p>
              </div>
            ) : (
              <div className="space-y-3">
                {historial.map(e => <EmergencyCard key={e.id_emergencia} e={e} showActions={e.estado !== 'resuelta'} />)}
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
}
